import { ReactNode, ButtonHTMLAttributes, CSSProperties } from 'react'

// ─── Eight-pointed star SVG ───────────────────────────────────────────────────

export const EightStar = ({
  size = 24,
  color = '#C9A34A',
  className = '',
  style,
}: {
  size?: number
  color?: string
  className?: string
  style?: CSSProperties
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill={color}
    className={className}
    style={style}
    aria-hidden
  >
    <polygon points="16,1 18.5,11.5 29,9 21.5,16 29,23 18.5,20.5 16,31 13.5,20.5 3,23 10.5,16 3,9 13.5,11.5" />
  </svg>
)

// ─── Engraving divider ────────────────────────────────────────────────────────

export const EngravingDivider = ({ className = '' }: { className?: string }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <div
      style={{
        flex: 1,
        height: 1,
        background: 'linear-gradient(to right, transparent, #C9A34A66, #C9A34A)',
      }}
    />
    <EightStar size={10} />
    <div
      style={{
        flex: 1,
        height: 1,
        background: 'linear-gradient(to left, transparent, #C9A34A66, #C9A34A)',
      }}
    />
  </div>
)

// ─── Gold CTA button ──────────────────────────────────────────────────────────

interface GoldButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode
  variant?: 'primary' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  loading?: boolean
}

export const GoldButton = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  className = '',
  ...props
}: GoldButtonProps) => {
  const sizeStyles = {
    sm: { padding: '10px 20px', fontSize: '0.8rem', letterSpacing: '0.12em' },
    md: { padding: '14px 32px', fontSize: '0.85rem', letterSpacing: '0.14em' },
    lg: { padding: '18px 48px', fontSize: '0.9rem', letterSpacing: '0.16em' },
  }

  const isPrimary = variant === 'primary'

  return (
    <button
      disabled={disabled || loading}
      className={`relative inline-flex items-center justify-center gap-2 font-mono uppercase tracking-widest transition-all duration-300 ${className}`}
      style={{
        ...sizeStyles[size],
        background: isPrimary
          ? 'linear-gradient(135deg, #C9A34A 0%, #E8C870 50%, #C9A34A 100%)'
          : 'transparent',
        backgroundSize: '200% 200%',
        border: isPrimary ? 'none' : '1px solid #C9A34A66',
        color: isPrimary ? '#09081A' : '#C9A34A',
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        opacity: disabled || loading ? 0.5 : 1,
        fontFamily: '"DM Mono", monospace',
        fontWeight: 500,
      }}
      {...props}
    >
      {loading && (
        <span
          style={{
            width: 14,
            height: 14,
            border: '2px solid #09081A44',
            borderTop: '2px solid #09081A',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            display: 'inline-block',
          }}
        />
      )}
      {children}
    </button>
  )
}

// ─── Gold-bordered card ───────────────────────────────────────────────────────

export const GoldCard = ({
  children,
  className = '',
  glow = false,
  style,
  onClick,
}: {
  children: ReactNode
  className?: string
  glow?: boolean
  style?: CSSProperties
  onClick?: () => void
}) => (
  <div
    className={className}
    onClick={onClick}
    style={{
      background: 'linear-gradient(135deg, #0D0C22 0%, #09081A 100%)',
      border: '1px solid #C9A34A33',
      boxShadow: glow
        ? '0 0 40px #C9A34A22, inset 0 1px 0 #C9A34A22'
        : 'inset 0 1px 0 #C9A34A11',
      borderRadius: 2,
      ...style,
    }}
  >
    {children}
  </div>
)

// ─── Seat counter badge ───────────────────────────────────────────────────────

export const SeatCounter = ({
  remaining,
  total = 888,
}: {
  remaining: number
  total?: number
}) => {
  const pct = ((total - remaining) / total) * 100

  return (
    <div className="inline-flex flex-col items-center gap-2">
      <div className="flex items-center gap-2">
        <EightStar size={8} />
        <span
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: '0.7rem',
            letterSpacing: '0.2em',
            color: '#C9A34A99',
            textTransform: 'uppercase',
          }}
        >
          Pioneer Seats Remaining
        </span>
        <EightStar size={8} />
      </div>
      <div
        style={{
          fontFamily: '"DM Mono", monospace',
          fontSize: '1.8rem',
          color: '#C9A34A',
          letterSpacing: '0.05em',
        }}
      >
        {remaining.toString().padStart(3, '0')}
        <span style={{ fontSize: '1rem', opacity: 0.4 }}> / {total}</span>
      </div>
      {/* Progress bar */}
      <div
        style={{
          width: 160,
          height: 2,
          background: '#C9A34A22',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: 'linear-gradient(to right, #C9A34A88, #C9A34A)',
            transition: 'width 1s ease',
          }}
        />
      </div>
    </div>
  )
}

// ─── Screen wrapper with starfield background ─────────────────────────────────

export const ScreenWrapper = ({
  children,
  className = '',
  style,
}: {
  children: ReactNode
  className?: string
  style?: CSSProperties
}) => (
  <div
    className={`min-h-screen relative overflow-hidden ${className}`}
    style={{ background: '#09081A', ...style }}
  >
    {/* Star field */}
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        backgroundImage: `
          radial-gradient(1px 1px at 20% 30%, #C9A34A44 0%, transparent 100%),
          radial-gradient(1px 1px at 80% 10%, #ffffff33 0%, transparent 100%),
          radial-gradient(1.5px 1.5px at 50% 70%, #C9A34A33 0%, transparent 100%),
          radial-gradient(1px 1px at 10% 90%, #ffffff22 0%, transparent 100%),
          radial-gradient(1px 1px at 90% 60%, #C9A34A22 0%, transparent 100%),
          radial-gradient(1px 1px at 35% 15%, #ffffff22 0%, transparent 100%),
          radial-gradient(1px 1px at 65% 85%, #C9A34A22 0%, transparent 100%),
          radial-gradient(1px 1px at 15% 55%, #ffffff11 0%, transparent 100%),
          radial-gradient(1px 1px at 75% 40%, #C9A34A11 0%, transparent 100%)
        `,
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
    {/* Grain texture */}
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E")`,
        opacity: 0.4,
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
    {/* Vignette */}
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        background: 'radial-gradient(ellipse at center, transparent 40%, #09081Aaa 100%)',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
    <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
  </div>
)

// ─── Progress dots ────────────────────────────────────────────────────────────

export const ProgressDots = ({ current, total = 5 }: { current: number; total?: number }) => (
  <div className="flex items-center gap-3 justify-center">
    {Array.from({ length: total }).map((_, i) => (
      <div
        key={i}
        style={{
          width: i + 1 === current ? 20 : 6,
          height: 6,
          borderRadius: 3,
          background: i + 1 <= current ? '#C9A34A' : '#C9A34A22',
          transition: 'all 0.4s ease',
        }}
      />
    ))}
  </div>
)

// ─── Gold input ───────────────────────────────────────────────────────────────

interface GoldInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string
  error?: string
}

export const GoldInput = ({ label, error, id, className = '', ...props }: GoldInputProps) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <label
      htmlFor={id}
      style={{
        fontFamily: '"DM Mono", monospace',
        fontSize: '0.65rem',
        letterSpacing: '0.18em',
        color: '#C9A34A99',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </label>
    <input
      id={id}
      style={{
        background: '#0D0C22',
        border: `1px solid ${error ? '#D4663A55' : '#C9A34A33'}`,
        color: '#F5E6C8',
        padding: '12px 16px',
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '1rem',
        outline: 'none',
        width: '100%',
        borderRadius: 2,
        transition: 'border-color 0.2s',
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = '#C9A34A'
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = error ? '#D4663A55' : '#C9A34A33'
      }}
      {...props}
    />
    {error && (
      <span style={{ fontSize: '0.75rem', color: '#D4663A', fontFamily: '"DM Sans", sans-serif' }}>
        {error}
      </span>
    )}
  </div>
)
